import React from 'react'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Link } from 'react-router-dom'
import { useCountUp, viz } from './overviewTheme'

// Fleet readiness — emphasis form: operational in the accent hue,
// off-the-road in the de-emphasis gray, both directly labelled
export const FleetReadinessCard = ({ fleet = {} }) => {
  const total = fleet.totalVehicles ?? 0
  const operational = fleet.operationalVehicles ?? 0
  const offRoad = fleet.offRoadVehicles ?? 0
  const readyPct = total === 0 ? 0 : Math.round((operational / total) * 100)
  const displayPct = useCountUp(readyPct)

  const serviceIssues =
    (fleet.vehicleServicesOverdue ?? 0) > 0 ||
    (fleet.vehicleServicesDueSoon ?? 0) > 0

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack>
        <Typography variant="title">Fleet readiness</Typography>
        <Typography variant="smallGrey">
          Vehicles available for operations
        </Typography>
      </Stack>
      <Typography
        component="div"
        sx={{ fontWeight: 600, fontSize: '3rem', lineHeight: 1.2, mt: 1 }}
      >
        {displayPct}%
      </Typography>
      {total > 0 && (
        <Stack direction="row" gap="2px" mt={1.5} borderRadius={5} overflow="hidden">
          {operational > 0 && (
            <Box
              sx={{
                height: 10,
                flexGrow: operational,
                backgroundColor: viz.series,
                borderRadius: '5px 0 0 5px',
              }}
            />
          )}
          {offRoad > 0 && (
            <Box
              sx={{
                height: 10,
                flexGrow: offRoad,
                backgroundColor: viz.deEmphasis,
                borderRadius: operational > 0 ? '0 5px 5px 0' : 5,
              }}
            />
          )}
        </Stack>
      )}
      <Stack direction="row" gap={2} mt={1}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: viz.series }} />
          <Typography variant="smallGrey">Operational {operational}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: viz.deEmphasis }} />
          <Typography variant="smallGrey">Off the road {offRoad}</Typography>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
        <Chip
          size="small"
          component={Link}
          to="/defects"
          clickable
          icon={
            (fleet.openDefects ?? 0) > 0 ? (
              <WarningAmberIcon />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
          color={(fleet.openDefects ?? 0) > 0 ? 'warning' : 'default'}
          variant="outlined"
          label={`${fleet.openDefects ?? 0} new defect${
            (fleet.openDefects ?? 0) === 1 ? '' : 's'
          }`}
        />
        <Chip
          size="small"
          icon={serviceIssues ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
          color={
            (fleet.vehicleServicesOverdue ?? 0) > 0
              ? 'error'
              : (fleet.vehicleServicesDueSoon ?? 0) > 0
                ? 'warning'
                : 'default'
          }
          variant="outlined"
          label={
            (fleet.vehicleServicesOverdue ?? 0) > 0
              ? `${fleet.vehicleServicesOverdue} service${
                  fleet.vehicleServicesOverdue === 1 ? '' : 's'
                } overdue`
              : (fleet.vehicleServicesDueSoon ?? 0) > 0
                ? `${fleet.vehicleServicesDueSoon} service${
                    fleet.vehicleServicesDueSoon === 1 ? '' : 's'
                  } due in 30 days`
                : 'Servicing up to date'
          }
        />
      </Stack>
    </Paper>
  )
}
